// ─── Chart helpers (shared) ──────────────────────────────────────────────────

/**
 * Format a date tick for a recharts XAxis. Implements DESIGN_RULES.md rule 8:
 *
 *   - Show month name only on the first tick of each month.
 *   - Show year on the first tick of each year.
 *   - Subsequent ticks within the same month show the day number only.
 *   - First and last visible ticks always render in full so the range is unambiguous.
 *
 * Pass into recharts:
 *   const fmt = makeDateTickFormatter(allDates)
 *   <XAxis tickFormatter={fmt} />
 */
export function makeDateTickFormatter(allTicks: string[]): (raw: string) => string {
  // Pre-compute month/year boundaries up-front so each call is O(1).
  const meta = allTicks.map((t) => {
    const d = new Date(t)
    return { raw: t, valid: !isNaN(d.getTime()), month: d.getMonth(), year: d.getFullYear(), day: d.getDate() }
  })

  return (raw: string) => {
    const idx = meta.findIndex((m) => m.raw === raw)
    if (idx === -1) return raw
    const m = meta[idx]!
    if (!m.valid) return raw

    const isFirst = idx === 0
    const isLast = idx === meta.length - 1
    const prev = idx > 0 ? meta[idx - 1] : null
    const isFirstOfMonth = !prev || prev.month !== m.month || prev.year !== m.year
    const isFirstOfYear = !prev || prev.year !== m.year

    const monthAbbr = MONTHS[m.month]

    if (isFirst || isLast || isFirstOfYear) {
      return `${monthAbbr} ${m.day} ${m.year}`
    }
    if (isFirstOfMonth) {
      return `${monthAbbr} ${m.day}`
    }
    return String(m.day)
  }
}

/**
 * Full date for tooltips — always unambiguous. Pair with makeDateTickFormatter
 * on the axis for the "axis is for orientation, tooltip is for precision" pattern.
 */
export function formatDateFull(raw: string): string {
  const d = new Date(raw)
  if (isNaN(d.getTime())) return raw
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
